import React, { useState } from 'react';
import { QRCode } from 'antd';
import { Card, Button, Input, Alert, Typography, Space, Steps, message } from 'antd';
import { CopyOutlined, DownloadOutlined, EyeOutlined, EyeInvisibleOutlined, MobileOutlined } from '@ant-design/icons';

const { Title, Text, Paragraph } = Typography;

interface TwoFactorSetupProps {
  qrCodeUrl: string;
  secret: string;
  backupCodes?: string[];
  onVerifySetup: (token: string) => Promise<boolean>;
  onComplete: (backupCodes: string[]) => void;
  isLoading?: boolean;
}

export const TwoFactorSetupAnt: React.FC<TwoFactorSetupProps> = ({
  qrCodeUrl,
  secret,
  backupCodes = [],
  onVerifySetup,
  onComplete,
  isLoading = false,
}) => {
  const [step, setStep] = useState<number>(0);
  const [verificationCode, setVerificationCode] = useState('');
  const [error, setError] = useState('');
  const [showSecret, setShowSecret] = useState(false);
  const [showBackupCodes, setShowBackupCodes] = useState(false);

  const copyToClipboard = async (text: string, type: 'secret' | 'backup') => {
    try {
      await navigator.clipboard.writeText(text);
      message.success(type === 'secret' ? 'Secret copied to clipboard' : 'Backup codes copied to clipboard');
    } catch (err) {
      message.error('Failed to copy to clipboard');
    }
  };

  const downloadBackupCodes = () => {
    const codesText = backupCodes.join('\n');
    const blob = new Blob([codesText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'brandscience-backup-codes.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleVerifySetup = async () => {
    if (!verificationCode.trim()) {
      setError('Please enter the verification code');
      return;
    }

    try {
      setError('');
      const success = await onVerifySetup(verificationCode);
      if (success) {
        // Always show backup codes step since they were generated during setup
        if (backupCodes.length > 0) {
          setStep(2);
        } else {
          // This shouldn't happen but handle gracefully
          onComplete([]);
        }
      }
    } catch (err) {
      setError('Invalid verification code. Please try again.');
    }
  };

  const handleComplete = () => {
    onComplete(backupCodes);
  };

  const steps = [
    {
      title: 'Scan',
      description: 'QR Code',
    },
    {
      title: 'Verify', 
      description: 'Code',
    },
    {
      title: 'Save',
      description: 'Backup Codes',
    },
  ];

  return (
    <div style={{ maxWidth: 500, margin: '0 auto' }}>
      <Steps 
        current={step} 
        items={steps} 
        style={{ 
          marginBottom: '2rem',
          padding: '0 8px'
        }}
        size="small"
        direction="horizontal"
      />
      
      <Card>
        {step === 0 && (
          <>
            <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
              <Title level={3}>Set up Two-Factor Authentication</Title>
              <Text type="secondary">
                Scan the QR code with your authenticator app
              </Text>
            </div>

            <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
              <div style={{ 
                display: 'inline-block',
                padding: '1rem',
                backgroundColor: '#fff',
                border: '1px solid #d9d9d9',
                borderRadius: '8px'
              }}>
                <QRCode value={qrCodeUrl} size={200} />
              </div>
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <Text type="secondary" style={{ fontSize: '12px', display: 'block', textAlign: 'center', marginBottom: '0.5rem' }}>
                Can't scan? Use this code instead:
              </Text>
              
              <Input.Group compact>
                <Input
                  type={showSecret ? 'text' : 'password'}
                  value={secret}
                  readOnly
                  style={{ fontFamily: 'monospace', fontSize: '12px' }}
                />
                <Button
                  icon={showSecret ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                  onClick={() => setShowSecret(!showSecret)}
                />
                <Button
                  icon={<CopyOutlined />}
                  onClick={() => copyToClipboard(secret, 'secret')}
                />
              </Input.Group>
            </div>

            <Alert
              message="Popular authenticator apps: Google Authenticator, Authy, Microsoft Authenticator, 1Password"
              type="info"
              icon={<MobileOutlined />}
              style={{ marginBottom: '1.5rem' }}
            />

            <Button type="primary" block onClick={() => setStep(1)}>
              I've Added the Account
            </Button>
          </>
        )}

        {step === 1 && (
          <>
            <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
              <Title level={3}>Verify Setup</Title>
              <Text type="secondary">
                Enter the 6-digit code from your authenticator app
              </Text>
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <Input
                type="text"
                placeholder="000000"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                style={{ 
                  textAlign: 'center', 
                  fontSize: '1.5rem', 
                  fontFamily: 'monospace',
                  height: '48px'
                }}
                maxLength={6}
                autoFocus
              />
              
              {error && (
                <Alert
                  message={error}
                  type="error"
                  style={{ marginTop: '1rem' }}
                />
              )}
            </div>

            <Space style={{ width: '100%' }}>
              <Button onClick={() => setStep(0)} style={{ flex: 1 }}>
                Back
              </Button>
              <Button
                type="primary"
                onClick={handleVerifySetup}
                disabled={isLoading || verificationCode.length !== 6}
                loading={isLoading}
                style={{ flex: 1 }}
              >
                {isLoading ? 'Verifying...' : 'Verify & Enable'}
              </Button>
            </Space>
          </>
        )}

        {step === 2 && (
          <>
            <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
              <Title level={3}>Save Backup Codes</Title>
              <Text type="secondary">
                Store these codes safely. You can use them to access your account if you lose your phone.
              </Text>
            </div>

            <Alert
              message="Each backup code can only be used once. Store them in a safe place like a password manager."
              type="warning"
              style={{ marginBottom: '1.5rem' }}
            />

            <div style={{ marginBottom: '1.5rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                <Text strong>Backup Codes</Text>
                <Button
                  size="small"
                  icon={showBackupCodes ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                  onClick={() => setShowBackupCodes(!showBackupCodes)}
                >
                  {showBackupCodes ? 'Hide' : 'Show'}
                </Button>
              </div>

              {showBackupCodes && (
                <>
                  <div style={{ 
                    backgroundColor: '#f5f5f5', 
                    padding: '1rem', 
                    borderRadius: '6px', 
                    fontFamily: 'monospace',
                    fontSize: '0.9rem',
                    marginBottom: '1rem'
                  }}>
                    {backupCodes.map((code, index) => (
                      <div key={index} style={{ padding: '2px 0' }}>{code}</div>
                    ))}
                  </div>

                  <Space style={{ width: '100%' }}>
                    <Button
                      icon={<CopyOutlined />}
                      onClick={() => copyToClipboard(backupCodes.join('\n'), 'backup')}
                      style={{ flex: 1 }}
                    >
                      Copy All
                    </Button>
                    <Button
                      icon={<DownloadOutlined />}
                      onClick={downloadBackupCodes}
                      style={{ flex: 1 }}
                    >
                      Download
                    </Button>
                  </Space>
                </>
              )}
            </div>

            <Button type="primary" block onClick={handleComplete}>
              I've Saved My Backup Codes
            </Button>
          </>
        )}
      </Card>
    </div>
  );
};